<template>
  <header class="qw-navbar">
    <!-- 品牌区：渐变 logo 图标 + 系统名，点击回到首页 -->
    <div class="qw-navbar__logo" @click="handleMenuClick('/userList')">
      <div class="qw-navbar__logo-icon">
        <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M12 2L2 7L12 12L22 7L12 2Z" fill="currentColor" opacity="0.8"/>
          <path d="M2 17L12 22L22 17" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          <path d="M2 12L12 17L22 12" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </div>
      <span class="qw-navbar__logo-text">企业微信会话存档</span>
    </div>

    <!-- 顶部模块菜单：5 项平铺，胶囊/渐变底线视觉态由 navbar-business.scss 的 qw-navbar__menu-item 接管 -->
    <nav class="qw-navbar__menu">
      <div
        v-for="item in menus"
        :key="item.path"
        class="qw-navbar__menu-item"
        :class="{ 'is-active': isMenuActive(item) }"
        @click="handleMenuClick(item.path)"
      >
        <i :class="item.icon"></i>
        <span>{{ item.title }}</span>
      </div>
    </nav>

    <!-- 右侧用户区：头像字母 + 用户名下拉（修改密码 / 退出登录） -->
    <div class="qw-navbar__right">
      <el-dropdown trigger="click" class="qw-navbar__user-dropdown" @command="handleAccountCommand">
        <div class="qw-navbar__user">
          <div class="qw-navbar__avatar">{{ avatarText }}</div>
          <span class="qw-navbar__username">{{ userName }}</span>
          <i class="el-icon-arrow-down"></i>
        </div>
        <el-dropdown-menu slot="dropdown">
          <el-dropdown-item command="password" icon="el-icon-lockplus">修改密码</el-dropdown-item>
          <el-dropdown-item divided command="logout" icon="el-icon-switch-buttonplus">退出登录</el-dropdown-item>
        </el-dropdown-menu>
      </el-dropdown>
    </div>

    <!-- 修改密码弹窗：壳样式走 element-ui.scss 全局 .el-dialog 商务主题，qw-password-dialog 仅作语义钩子 -->
    <el-dialog
      title="修改密码"
      :visible.sync="pwdDialogVisible"
      width="440px"
      :close-on-click-modal="false"
      append-to-body
      custom-class="qw-password-dialog"
    >
      <el-form ref="pwdForm" :model="pwdForm" :rules="pwdRules" label-width="90px" size="small">
        <el-form-item label="原密码" prop="oldPassword">
          <el-input v-model="pwdForm.oldPassword" type="password" show-password placeholder="请输入原密码" />
        </el-form-item>
        <el-form-item label="新密码" prop="newPassword">
          <el-input v-model="pwdForm.newPassword" type="password" show-password placeholder="至少 6 位" />
        </el-form-item>
        <el-form-item label="确认密码" prop="confirmPassword">
          <el-input v-model="pwdForm.confirmPassword" type="password" show-password placeholder="再次输入新密码" />
        </el-form-item>
      </el-form>
      <template slot="footer">
        <el-button size="small" @click="pwdDialogVisible = false">取 消</el-button>
        <el-button size="small" type="primary" :loading="pwdSubmitting" @click="submitChangePassword">确 定</el-button>
      </template>
    </el-dialog>
  </header>
</template>

<script>
import { adminChangePassword } from '@/api/admin'

// 顶部导航：品牌位 + 平铺模块菜单 + 用户下拉（修改密码 / 退出登录）
// 结构对齐参考项目 ReportManagement_pc：div.qw-navbar__menu-item + el-dropdown，不用 el-menu
export default {
    name: 'Navbar',
    data () {
        // 两次输入的新密码必须一致
        const validateConfirm = (rule, value, callback) => {
            if (value !== this.pwdForm.newPassword) {
                callback(new Error('两次输入的新密码不一致'))
            } else {
                callback()
            }
        }
        return {
            menus: [
                { path: '/userList', title: '用户管理', icon: 'el-icon-userplus' },
                { path: '/appList', title: '接入方管理', icon: 'el-icon-connectionplus' },
                { path: '/groupList', title: '客户群查询', icon: 'el-icon-chat-line-roundplus' },
                { path: '/messageList', title: '消息查询', icon: 'el-icon-messageplus' },
                { path: '/archiveStatus', title: '存档状态', icon: 'el-icon-data-analysisplus' }
            ],
            pwdDialogVisible: false,
            pwdSubmitting: false,
            pwdForm: {
                oldPassword: '',
                newPassword: '',
                confirmPassword: ''
            },
            pwdRules: {
                oldPassword: [{ required: true, message: '请输入原密码', trigger: 'blur' }],
                newPassword: [
                    { required: true, message: '请输入新密码', trigger: 'blur' },
                    { min: 6, message: '新密码至少 6 位', trigger: 'blur' }
                ],
                confirmPassword: [
                    { required: true, message: '请再次输入新密码', trigger: 'blur' },
                    { validator: validateConfirm, trigger: 'blur' }
                ]
            }
        }
    },
    computed: {
        userInfo () {
            return this.$store.state.user.userInfo || {}
        },
        userName () {
            return this.userInfo.realName || this.userInfo.username || '管理员'
        },
        avatarText () {
            const name = this.userName
            return name ? name.charAt(0).toUpperCase() : 'A'
        }
    },
    methods: {
        // 菜单选中态：平铺菜单与当前路由精确匹配（参考项目 isMenuActive 的无子级简化版）
        isMenuActive (item) {
            return this.$route.path === item.path
        },
        // 菜单/品牌位跳转：同路由不重复导航，吞掉重复导航异常
        handleMenuClick (path) {
            if (path && path !== this.$route.path) {
                this.$router.push(path).catch(() => {})
            }
        },
        handleAccountCommand (command) {
            if (command === 'password') {
                this.pwdForm = { oldPassword: '', newPassword: '', confirmPassword: '' }
                this.pwdDialogVisible = true
                this.$nextTick(() => {
                    this.$refs.pwdForm && this.$refs.pwdForm.clearValidate()
                })
                return
            }
            if (command === 'logout') {
                this.handleLogout()
            }
        },
        async handleLogout () {
            try {
                await this.$confirm('确定退出登录吗？', '提示', { type: 'warning' })
            } catch (e) {
                return
            }
            await this.$store.dispatch('user/logout')
            this.$router.push('/login')
        },
        // 修改密码：成功后仅提示（原令牌仍有效，无需强制重新登录）；失败提示由 request 拦截器统一弹出
        submitChangePassword () {
            this.$refs.pwdForm.validate(async valid => {
                if (!valid) return
                this.pwdSubmitting = true
                try {
                    await adminChangePassword({
                        oldPassword: this.pwdForm.oldPassword,
                        newPassword: this.pwdForm.newPassword
                    })
                    this.$message.success('密码修改成功')
                    this.pwdDialogVisible = false
                } catch (error) {
                    // 错误提示已由 request 拦截器统一处理
                } finally {
                    this.pwdSubmitting = false
                }
            })
        }
    }
}
</script>

<style lang="scss" scoped>
// 基础骨架层：只写布局结构；颜色/胶囊/渐变底线等视觉态由 navbar-business.scss 以 !important 覆盖
.qw-navbar {
    height: 56px;
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    z-index: 100;
    display: flex;
    align-items: center;
    background: var(--qw-bg-card);
    border-bottom: 1px solid var(--qw-border-light);
    box-shadow: var(--qw-shadow-sm);
    padding: 0 16px 0 12px;
}

// 品牌区
.qw-navbar__logo {
    display: flex;
    align-items: center;
    gap: 10px;
    cursor: pointer;
    padding-right: 20px;
    border-right: 1px solid var(--qw-border-light);
    margin-right: 16px;
}

.qw-navbar__logo-icon {
    width: 32px;
    height: 32px;
    // 体系蓝→靛→青品牌渐变（与参考项目 logo、page-container 顶部渐变条一致）
    background: linear-gradient(135deg, #2563eb 0%, #4f46e5 52%, #06b6d4 100%);
    border-radius: var(--qw-radius-lg);
    display: flex;
    align-items: center;
    justify-content: center;
    color: #fff;
    flex-shrink: 0;

    svg {
        width: 18px;
        height: 18px;
    }
}

.qw-navbar__logo-text {
    font-size: 16px;
    font-weight: 600;
    color: var(--qw-gray-800);
    white-space: nowrap;
}

// 顶部菜单容器：撑满靠右排布
.qw-navbar__menu {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 4px;
    overflow: hidden;
}

// 菜单项基础结构（胶囊视觉态在 navbar-business.scss）
.qw-navbar__menu-item {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 14px;
    cursor: pointer;
    white-space: nowrap;
    transition: color 0.25s ease, background 0.25s ease, box-shadow 0.25s ease;
}

// 右侧工具区
.qw-navbar__right {
    display: flex;
    align-items: center;
    gap: 8px;
    padding-left: 20px;
    border-left: 1px solid var(--qw-gray-200);
}

// 用户下拉触发器
.qw-navbar__user {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 6px 12px 6px 8px;
    border-radius: 20px;
    cursor: pointer;
    transition: background 0.2s;
}

.qw-navbar__avatar {
    width: 32px;
    height: 32px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    // 与品牌 logo 同款体系渐变
    background: linear-gradient(135deg, #2563eb 0%, #4f46e5 52%, #06b6d4 100%);
    color: #fff;
    font-weight: 600;
    font-size: 14px;
    overflow: hidden;
    flex-shrink: 0;
}

.qw-navbar__username {
    font-size: 14px;
    font-weight: 500;
    color: var(--qw-gray-700);
    max-width: 120px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}
</style>
